import { z } from 'zod'
import { isMoney, toMoney, type Money } from '../money.ts'

/**
 * ══ VIÊN GẠCH DÙNG CHUNG CỦA MỌI HỢP ĐỒNG API (KIEN-TRUC §7) ══════════════════════════════════
 *
 * Ba kiểu dưới đây xuất hiện ở gần như mọi endpoint. Định nghĩa MỘT lần ở đây rồi ghép lại, thay
 * vì mỗi tệp hợp đồng tự viết `z.string().regex(...)` của riêng nó — hai bản viết tay sẽ lệch nhau
 * đúng vào ngày ai đó siết một bên mà quên bên kia.
 *
 * ⚠ Đây là lược đồ **BIÊN API**, không phải lược đồ CSDL. Chúng siết CHẶT HƠN kiểu miền: `Money`
 *   của `money.ts` chấp nhận số bao nhiêu chữ số cũng được, còn ở biên phải khớp `NUMERIC(20,2)` —
 *   nếu không, một số 25 chữ số lọt qua tầng kiểm rồi mới vỡ lúc ghi CSDL, và lúc đó thông báo
 *   cho người dùng là một câu lỗi Postgres.
 */

// NUMERIC(20,2): tối đa 18 chữ số phần nguyên, tối đa 2 chữ số thập phân. Cùng ràng buộc mà
// `order-out.ts` đã đặt ra cho đầu RA; ở đây áp cho đầu VÀO. Cố ý KHÔNG chép lại `DEC_RE` của
// money.ts — dùng lại `isMoney` rồi siết thêm.
const NUMERIC_20_2 = /^-?\d{1,18}(\.\d{1,2})?$/

/**
 * Tiền — CHUỖI thập phân, không bao giờ `number`. Đầu vào được CHUẨN HOÁ qua `toMoney()` (đúng 2
 * chữ số thập phân), không phải ép kiểu suông: `'100'` phải thành `'100.00'` ngay tại biên, nếu
 * không thì so sánh chuỗi và khoá gom nhóm ở tầng dưới lệch nhau.
 */
export const MoneyIn: z.ZodType<Money, z.ZodTypeDef, unknown> = z
  .string({ invalid_type_error: 'Tiền phải là chuỗi thập phân, không phải số.' })
  .refine(isMoney, { message: 'Không phải số tiền hợp lệ (cần dạng 45200000 hoặc 45200000.50).' })
  .refine((v) => NUMERIC_20_2.test(v), {
    message: 'Tiền vượt phạm vi CSDL: tối đa 18 chữ số phần nguyên và 2 chữ số thập phân.',
  })
  .transform((v) => toMoney(v))

/**
 * Tiền có NHÃN TIẾNG VIỆT của ô nhập.
 *
 * ⚠ VÌ SAO CẦN NHÃN, KHÔNG DÙNG THẲNG `MoneyIn`: người dùng cuối là kế toán Việt Nam. Một thông
 *   báo `"sellPrice": không phải số tiền hợp lệ` là vô nghĩa với họ — họ không biết ô nào trên màn
 *   hình tên `sellPrice`. Tầng nghiệp vụ đã đặt đúng nhãn tiếng Việt từ trước (`parseMoney('Giá
 *   chào khách', …)`); chuyển việc kiểm lên biên mà bỏ nhãn lại là ĐỔI XUỐNG một trải nghiệm tệ
 *   hơn để lấy một kiến trúc đẹp hơn. Giữ cả hai: kiểm ở biên, nhãn vẫn tiếng Việt.
 *
 * Phép kiểm ở tầng nghiệp vụ **KHÔNG bỏ đi** — nó vẫn là lớp phòng vệ thứ hai cho mọi lời gọi
 * không đi qua HTTP (seed, việc nền, test tích hợp gọi thẳng use case).
 */
export const moneyField = (label: string): z.ZodType<Money, z.ZodTypeDef, unknown> => z
  .string({ invalid_type_error: `${label}: phải là chuỗi thập phân, không phải số.` })
  .refine(isMoney, { message: `${label}: không phải số hợp lệ (cần dạng 45200000 hoặc 45200000.50).` })
  .refine((v) => NUMERIC_20_2.test(v), {
    message: `${label}: vượt phạm vi CSDL — tối đa 18 chữ số phần nguyên và 2 chữ số thập phân.`,
  })
  .transform((v) => toMoney(v))

/**
 * Tiền ở ĐẦU RA — chỉ khẳng định hình dạng, KHÔNG chuẩn hoá lại (dữ liệu đã chuẩn từ CSDL).
 *
 * ⚠ Kiểu ra là `string` TRẦN, cố ý KHÔNG mang nhãn `Money`. Nhãn `Money` sinh ra để chặn phép toán
 *   dấu phẩy động **trong tầng miền** — nơi cộng trừ tiền thật. Trên DÂY thì tiền chỉ là một chuỗi
 *   thập phân, và `apps/web` chỉ hiển thị nó. Gắn nhãn ở đây bắt mọi ô nhập của màn hình phải đi
 *   qua `toMoney()` chỉ để gán vào một trường sắp bị `JSON.stringify` — đổi lấy phiền phức mà
 *   không chặn thêm được lỗi nào, vì tầng miền vẫn kiểm lại toàn bộ khi nhận.
 */
export const MoneyOut: z.ZodType<string, z.ZodTypeDef, unknown> = z
  .string()
  .refine(isMoney, { message: 'Không phải số tiền hợp lệ.' })

/**
 * Ngày `YYYY-MM-DD` neo giờ Việt Nam (§3 CLAUDE.md).
 *
 * ⚠ KHÔNG dùng `z.string().date()` của zod: nó chấp nhận `'2026-02-30'` ở một số bản và không
 *   kiểm ngày có thật. Ở đây kiểm cả hình dạng LẪN tính hợp lệ, vì `'2026-02-30'` lọt xuống CSDL
 *   là một lỗi Postgres thô ra thẳng màn hình người dùng.
 */
export const ISODate: z.ZodType<string, z.ZodTypeDef, unknown> = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, { message: 'Ngày phải có dạng YYYY-MM-DD.' })
  .refine((v) => {
    const [y, m, d] = v.split('-').map(Number) as [number, number, number]
    const dt = new Date(Date.UTC(y, m - 1, d))
    return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d
  }, { message: 'Ngày không có thật trên lịch.' })

/** Thời điểm ISO kèm múi giờ. */
export const ISOTimestamp: z.ZodType<string, z.ZodTypeDef, unknown> = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,6})?(Z|[+-]\d{2}:\d{2})$/, {
    message: 'Thời điểm phải là ISO 8601 kèm múi giờ.',
  })

export const Uuid = z.string().uuid({ message: 'Không phải mã định danh hợp lệ.' })

/**
 * Số hiệu bản ghi cho khoá lạc quan (A4). Bắt buộc ở MỌI lời gọi ghi — thiếu nó thì người ghi sau
 * xoá mất việc của người ghi trước mà không ai biết.
 */
export const Version = z.number().int().nonnegative({
  message: 'Thiếu `version` — không có nó thì không phát hiện được xung đột ghi.',
})

/**
 * ══ Ô ĐỂ TRỐNG TRÊN FORM = "KHÔNG ĐẶT", KHÔNG PHẢI "CHUỖI RỖNG" (KIEN-TRUC §7) ════════════════
 *
 * `<input>` của HTML KHÔNG có khái niệm "vắng mặt": ô ngày người dùng chưa điền cho ra `''`, và
 * `JSON.stringify` gửi nguyên chuỗi rỗng đó lên. Với một lược đồ `ISODate.optional()`, `''` KHÔNG
 * phải `undefined` nên nó rơi vào nhánh kiểm định dạng và bị từ chối — người dùng thấy
 * *"`effectiveTo`: Ngày phải có dạng YYYY-MM-DD"* cho một ô họ **cố ý bỏ trống**.
 *
 * ⚠ Đây ĐÚNG bằng lỗi mà `config/env.ts` phải vá cho biến môi trường (Compose truyền `''` khi
 *   `.env` thiếu biến) — cùng một hiểu lầm, hai tầng khác nhau: **chuỗi rỗng là CHƯA ĐẶT**.
 *   Đã tái lập thật: E2E `"thêm dòng đơn giá"` đỏ vì ô "Hiệu lực đến" để trống.
 *
 * ⛔ KHÔNG sửa bằng cách để màn hình tự lọc ô rỗng trước khi gửi. Làm thế là đặt luật ở màn, và
 *   màn thứ hai sẽ quên (§4: máy chủ là nơi duy nhất quyết định). Chuẩn hoá ở BIÊN, một lần.
 */
/**
 * ⚠ THỨ TỰ BỌC QUAN TRỌNG: `preprocess(fn, schema.optional())`, KHÔNG phải
 *   `preprocess(fn, schema).optional()`. Với thứ tự sau, lớp `.optional()` nằm NGOÀI: nó thấy
 *   `''` (khác `undefined`) nên chuyển thẳng xuống `preprocess`, hàm này trả `undefined`, rồi
 *   lược đồ BÊN TRONG — vốn không optional — từ chối `undefined`. Kết quả: vẫn đỏ, chỉ đổi câu
 *   lỗi. Đã tái lập bằng ca kiểm ngay bên dưới.
 */
export const blankAsAbsent = <T extends z.ZodTypeAny>(
  schema: T,
): z.ZodType<z.output<T> | undefined, z.ZodTypeDef, unknown> =>
  z.preprocess(
    (v) => (typeof v === 'string' && v.trim() === '' ? undefined : v),
    (schema as z.ZodTypeAny).optional(),
  ) as z.ZodType<z.output<T> | undefined, z.ZodTypeDef, unknown>

/**
 * ══ CỜ TRÊN CHUỖI TRUY VẤN — `?includeDraft=false` phải ra `false` ════════════════════════════
 *
 * ⚠ ĐỪNG DÙNG `z.coerce.boolean()` cho tham số URL. Nó gọi `Boolean(v)`, và `Boolean('false')` là
 *   **true** — mọi giá trị chuỗi khác rỗng đều thành `true`. Cổng `?includeDraft=false` khi đó
 *   không lọc gì cả, và không có lỗi nào: danh sách chỉ đơn giản trả về nhiều hơn người dùng
 *   tưởng. Bộ kiểm route của F10 bắt được đúng lỗi này ngay lần chạy đầu.
 *
 * Nhận `true|1|yes` và `false|0|no` (không phân biệt hoa thường); giá trị khác ⇒ báo lỗi rõ ràng
 * thay vì đoán bừa.
 */
export const BoolQuery: z.ZodType<boolean, z.ZodTypeDef, unknown> = z
  .union([z.boolean(), z.string()])
  .transform((v, ctx) => {
    if (typeof v === 'boolean') return v
    const s = v.trim().toLowerCase()
    if (s === 'true' || s === '1' || s === 'yes') return true
    if (s === 'false' || s === '0' || s === 'no') return false
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Chỉ nhận true/false (hoặc 1/0).' })
    return z.NEVER
  })

/**
 * Ô SỬA ĐƯỢC VỀ TRỐNG — cho lược đồ SỬA, không cho lược đồ TẠO.
 *
 * ⭐ KHÁC `blankAsAbsent` Ở ĐÚNG MỘT CHỖ, và chỗ đó là cả lý do tồn tại. `blankAsAbsent` biến `''`
 *   thành VẮNG MẶT — đúng cho form TẠO (bỏ trống = không đặt), nhưng SAI cho form SỬA: kế toán xoá
 *   trắng ô "Số hóa đơn" là muốn XOÁ nó, mà vắng mặt lại có nghĩa "giữ nguyên". Ở đây `''` và `null`
 *   cùng nghĩa XOÁ; vắng khoá mới là giữ nguyên.
 */
export const clearable = <T extends z.ZodTypeAny>(schema: T) =>
  z.union([schema, z.literal(''), z.null()]).optional()
